import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Building2,
    Mail,
    User,
    Globe,
    MessageSquare,
    CheckCircle2,
    ArrowRight,
    Globe2,
    Heart,
    Users,
    Building,
    Anchor,
    ShieldCheck,
    Loader2
} from 'lucide-react';
import { createClientComponentClient } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const NGO_TYPES = [
    { id: 'charity', label: 'Registered Charity', icon: <Heart size={18} /> },
    { id: 'ngo', label: 'Non-Governmental Organisation', icon: <Globe size={18} /> },
    { id: 'social_enterprise', label: 'Social Enterprise', icon: <Users size={18} /> },
    { id: 'community', label: 'Community Project', icon: <Building size={18} /> },
    { id: 'other', label: 'Other / Foundation', icon: <Anchor size={18} /> },
];

export default function NgoApplyPage() {
  const { t } = useTranslation();

    const supabase = createClientComponentClient();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        website: '',
        country: '',
        organisation_type: 'charity',
        cause_area: '',
        application_message: '',
        charity_number: ''
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);

        try {
            const { data, error } = await supabase.functions.invoke('apply-ngo', {
                body: {
                    ...formData,
                    contact_email: formData.contact_email.trim().toLowerCase(),
                },
            });
            if (error) throw error;
            if (data?.error) throw new Error(data.error);
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (error: any) {
            alert(error.message || "Failed to submit application. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    if (submitted) {
        return (
            <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="max-w-md w-full bg-white rounded-3xl shadow-xl shadow-slate-200/60 p-10 text-center border border-slate-100"
                >
                    <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 size={40} />
                    </div>
                    <h1 className="text-3xl font-black text-slate-900 mb-4">{t('ngo.you_re_in_partner_dashboard_ready')}</h1>
                    <p className="text-slate-600 leading-relaxed mb-8 font-medium">
                        Your organisation is verified. Sign up or log in with <strong>{formData.contact_email}</strong> and you'll land straight on your partner dashboard: nominate your learners, then request sponsored seats or purchase them instantly at the matched rate.
                    </p>
                    <button
                        onClick={() => navigate('/ngo/dashboard')}
                        className="w-full py-4 bg-emerald-600 text-white font-bold rounded-2xl hover:bg-emerald-700 transition-all flex items-center justify-center gap-2 group mb-3"
                    >
                        Open My Partner Dashboard
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                    <button
                        onClick={() => navigate('/')}
                        className="w-full py-4 bg-slate-900 text-white font-bold rounded-2xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2 group"
                    >
                        Return Home
                        <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </button>
                </motion.div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#fcfcfd] pb-20">
            {/* Header Section */}
            <div className="bg-white border-b border-slate-100 pt-20 pb-16 px-6">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-full text-sm font-bold mb-6"
                    >
                        <ShieldCheck size={16} />
                        Partner Portal Application
                    </motion.div>
                    <motion.h1
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-4xl md:text-5xl font-black text-slate-900 mb-6 tracking-tight"
                    >
                        Empower your community with <br /><span className="text-emerald-600">{t('ngo.adaptive_ai_education')}</span>
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                        className="text-lg text-slate-600 max-w-2xl mx-auto font-medium"
                    >
                        Join our NFP/NGO outreach program to provide high-quality education to children in need. Apply below to request your dedicated partner dashboard.
                    </motion.p>
                </div>
            </div>

            {/* Form Section */}
            <div className="max-w-3xl mx-auto px-6 -mt-8">
                <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-[32px] shadow-2xl shadow-slate-200/50 border border-slate-100 p-8 md:p-12"
                >
                    <form onSubmit={handleSubmit} className="space-y-8">

                        {/* Organisation Details */}
                        <section className="space-y-6">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                                    <Building2 size={18} />
                                </div>
                                <h3 className="text-xl font-black text-slate-900">{t('ngo.organisation_info')}</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.organisation_name')}</label>
                                    <input
                                        required
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="e.g. Hope Foundation"
                                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.organisation_type')}</label>
                                    <select
                                        name="organisation_type"
                                        value={formData.organisation_type}
                                        onChange={handleChange}
                                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none appearance-none cursor-pointer"
                                    >
                                        {NGO_TYPES.map(type => (
                                            <option key={type.id} value={type.id}>{type.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.country')}</label>
                                    <input
                                        required
                                        name="country"
                                        value={formData.country}
                                        onChange={handleChange}
                                        placeholder="e.g. Australia, USA, Kenya"
                                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.charity_reg')}</label>
                                    <input
                                        name="charity_number"
                                        value={formData.charity_number}
                                        onChange={handleChange}
                                        placeholder="Optional"
                                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.website_social')}</label>
                                    <div className="relative">
                                        <Globe2 size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
                                        <input
                                            name="website"
                                            value={formData.website}
                                            onChange={handleChange}
                                            placeholder="https://..."
                                            className="w-full pl-12 pr-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Contact Details */}
                        <section className="space-y-6 pt-4">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                                    <User size={18} />
                                </div>
                                <h3 className="text-xl font-black text-slate-900">{t('ngo.lead_contact')}</h3>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.full_name')}</label>
                                    <input
                                        required
                                        name="contact_name"
                                        value={formData.contact_name}
                                        onChange={handleChange}
                                        placeholder={t('ngo.official_representative')}
                                        className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.official_email')}</label>
                                    <div className="relative">
                                        <Mail size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300" />
                                        <input
                                            required
                                            type="email"
                                            name="contact_email"
                                            value={formData.contact_email}
                                            onChange={handleChange}
                                            placeholder="organisation@email.com"
                                            className="w-full pl-12 pr-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none"
                                        />
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* Mission Details */}
                        <section className="space-y-6 pt-4">
                            <div className="flex items-center gap-3 mb-2">
                                <div className="w-8 h-8 bg-slate-100 rounded-lg flex items-center justify-center text-slate-400">
                                    <MessageSquare size={18} />
                                </div>
                                <h3 className="text-xl font-black text-slate-900">{t('ngo.your_mission')}</h3>
                            </div>

                            <div>
                                <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.who_do_you_serve')}</label>
                                <input
                                    required
                                    name="cause_area"
                                    value={formData.cause_area}
                                    onChange={handleChange}
                                    placeholder={t('ngo.orphans_in_rural_bali_disadvantaged_youth')}
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-black text-slate-700 uppercase tracking-widest mb-2">{t('ngo.application_message')}</label>
                                <textarea
                                    required
                                    name="application_message"
                                    value={formData.application_message}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="Tell us about your organisation and how access to Still Waters Academy will help your community..."
                                    className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-2xl text-slate-900 font-bold placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 transition-all outline-none resize-none"
                                />
                            </div>
                        </section>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-5 bg-emerald-600 text-white font-black text-lg rounded-[20px] shadow-xl shadow-emerald-200 hover:bg-emerald-700 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 disabled:bg-slate-300 disabled:shadow-none"
                        >
                            {loading ? (
                                <><Loader2 size={24} className="animate-spin" /> Submitting...</>
                            ) : (
                                <><CheckCircle2 size={24} /> Submit Application</>
                            )}
                        </button>

                        <p className="text-center text-slate-400 text-xs font-bold uppercase tracking-widest">
                            By submitting, you agree to our Partner Terms of Service.
                        </p>
                    </form>
                </motion.div>
            </div>
        </div>
    );
}
